import React from 'react';
import { motion } from 'framer-motion';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 0.9, 0.32, 1] } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

/** Hero used for inner marketing pages — cream background, eyebrow + serif title + intro. */
export function PageHero({ eyebrow, title, intro, children }) {
  return (
    <section className="relative bg-tge-paper pt-[120px] pb-16 lg:pb-24">
      <div className="max-w-5xl mx-auto px-6">
        <motion.div initial="hidden" animate="show" variants={stagger}>
          {eyebrow && (
            <motion.span variants={fadeUp} className="text-tge-gold text-xs uppercase tracking-[0.18em] font-semibold">
              {eyebrow}
            </motion.span>
          )}
          <motion.h1 variants={fadeUp} className="font-serif-display text-5xl sm:text-6xl text-tge-navy leading-[1.05] mt-3">
            {title}
          </motion.h1>
          {intro && (
            <motion.p variants={fadeUp} className="mt-6 text-lg text-tge-muted max-w-3xl leading-relaxed">
              {intro}
            </motion.p>
          )}
          {children}
        </motion.div>
      </div>
    </section>
  );
}

/** Card grid section. Items: { Icon, title, desc, badge?, items? } */
export function FeatureGrid({ items, columns = 3 }) {
  const cols = columns === 2 ? 'md:grid-cols-2' : columns === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3';
  return (
    <section className="bg-white">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-50px' }}
          variants={stagger}
          className={`grid ${cols} gap-6`}
        >
          {items.map((it, i) => (
            <motion.article
              key={it.title || i}
              variants={fadeUp}
              className="bg-tge-cream-soft border border-transparent hover:border-tge-gold/30 rounded-3xl p-7 transition-all hover:shadow-[0_8px_32px_rgba(27,42,107,0.08)]"
            >
              {it.Icon && (
                <div className="w-12 h-12 rounded-xl bg-tge-navy text-white flex items-center justify-center mb-5">
                  <it.Icon size={22} strokeWidth={1.7} />
                </div>
              )}
              {it.badge && (
                <span className="inline-block text-[11px] font-semibold uppercase tracking-wider text-tge-gold mb-2">
                  {it.badge}
                </span>
              )}
              <h3 className="font-serif-display text-2xl text-tge-navy mb-3">{it.title}</h3>
              <p className="text-tge-muted text-[15px] leading-relaxed">{it.desc}</p>
              {it.items && (
                <ul className="mt-5 space-y-2">
                  {it.items.map((sub) => (
                    <li key={sub} className="text-sm text-tge-navy flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-tge-gold mt-2 flex-shrink-0" /> {sub}
                    </li>
                  ))}
                </ul>
              )}
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/** Simple two-column "Why us" or "Our process" with serif heading. */
export function ProseSection({ eyebrow, title, children, dark }) {
  return (
    <section className={dark ? 'bg-tge-navy text-tge-cream' : 'bg-tge-cream'}>
      <div className="max-w-5xl mx-auto px-6 py-20">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
          {eyebrow && (
            <motion.span variants={fadeUp} className={`text-xs uppercase tracking-[0.18em] font-semibold ${dark ? 'text-tge-gold' : 'text-tge-gold'}`}>
              {eyebrow}
            </motion.span>
          )}
          <motion.h2 variants={fadeUp} className={`font-serif-display text-4xl sm:text-5xl mt-3 ${dark ? 'text-white' : 'text-tge-navy'}`}>
            {title}
          </motion.h2>
          <motion.div variants={fadeUp} className={`prose prose-lg mt-8 max-w-none ${dark ? 'prose-invert' : ''}`}>
            {children}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/** Final CTA block. */
export function CTABand({ title = 'Ready to take the next step?', body = 'Book a free 30-minute consultation. No pressure, just clarity.', primary, secondary }) {
  return (
    <section className="bg-tge-navy text-white">
      <div className="max-w-5xl mx-auto px-6 py-20 text-center relative overflow-hidden">
        <div className="absolute -left-20 -top-20 w-72 h-72 bg-tge-gold/15 rounded-full blur-3xl" />
        <div className="absolute -right-20 -bottom-20 w-72 h-72 bg-tge-gold/10 rounded-full blur-3xl" />
        <div className="relative">
          <h2 className="font-serif-display text-4xl sm:text-5xl">{title}</h2>
          <p className="text-tge-cream/75 mt-5 text-lg max-w-xl mx-auto">{body}</p>
          <div className="mt-9 flex flex-wrap gap-4 justify-center">
            {primary}
            {secondary}
          </div>
        </div>
      </div>
    </section>
  );
}
